import React, { useState, useEffect } from 'react';
import { Text } from './ui';
import TimerIcon from './ui/TimerIcon';

const colorTokens = {
    alabaster: '#EEEEE7',
    white: '#FFFFFF',
    oxfordBlue: '#1b263b',
    richBlack: '#0d1b2a',
    notecardBlue: '#4A90E2',
    red: '#DC2626',
    neutral: {
        0: '#FFFFFF',
        50: '#F9F8F6',
        900: '#0D0D0D'
    }
};

// TimerEditModal component for editing step timers
function TimerEditModal({ open, onClose, onSave, onRemove, stepIndex, timingSegments = [] }) {
    let segments = timingSegments;
    if (typeof timingSegments === 'string') {
        try {
            segments = JSON.parse(timingSegments);
        } catch (e) {
            segments = [];
        }
    }
    
    if (!Array.isArray(segments)) {
        segments = [];
    }

    const activeSegment = segments.find(s => s.kind === 'active') || {
        kind: 'active',
        label: 'Active time',
        expectedSeconds: 0
    };
    const inactiveSegment = segments.find(s => s.kind === 'inactive') || {
        kind: 'inactive',
        label: 'Inactive time',
        expectedSeconds: 0
    };

    const [activeMinutes, setActiveMinutes] = useState(Math.floor(activeSegment.expectedSeconds / 60));
    const [activeSeconds, setActiveSeconds] = useState(activeSegment.expectedSeconds % 60);
    const [activeLabel, setActiveLabel] = useState(activeSegment.label || 'Active time');
    
    const [inactiveMinutes, setInactiveMinutes] = useState(Math.floor(inactiveSegment.expectedSeconds / 60));
    const [inactiveSeconds, setInactiveSeconds] = useState(inactiveSegment.expectedSeconds % 60);
    const [inactiveLabel, setInactiveLabel] = useState(inactiveSegment.label || 'Inactive time');

    useEffect(() => {
        if (open) {
            let currentSegments = timingSegments;
            if (typeof timingSegments === 'string') {
                try {
                    currentSegments = JSON.parse(timingSegments);
                } catch (e) {
                    currentSegments = [];
                }
            }
            if (!Array.isArray(currentSegments)) {
                currentSegments = [];
            }
            
            const active = currentSegments.find(s => s.kind === 'active') || { expectedSeconds: 0, label: 'Active time' };
            const inactive = currentSegments.find(s => s.kind === 'inactive') || { expectedSeconds: 0, label: 'Inactive time' };
            
            setActiveMinutes(Math.floor(active.expectedSeconds / 60));
            setActiveSeconds(active.expectedSeconds % 60);
            setActiveLabel(active.label || 'Active time');
            
            setInactiveMinutes(Math.floor(inactive.expectedSeconds / 60));
            setInactiveSeconds(inactive.expectedSeconds % 60);
            setInactiveLabel(inactive.label || 'Inactive time');
        }
    }, [open, timingSegments]);

    const handleSave = () => {
        const activeTotalSeconds = activeMinutes * 60 + activeSeconds;
        const inactiveTotalSeconds = inactiveMinutes * 60 + inactiveSeconds;
        
        const newSegments = [
            {
                kind: 'active',
                label: activeLabel,
                expectedSeconds: activeTotalSeconds,
                order: 0
            },
            {
                kind: 'inactive',
                label: inactiveLabel,
                expectedSeconds: inactiveTotalSeconds,
                order: 1
            }
        ];
        
        onSave(newSegments);
    };

    if (!open) return null;
    
    const totalMinutes = activeMinutes + inactiveMinutes;
    const totalSeconds = activeSeconds + inactiveSeconds;
    
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-lg shadow-lg p-6 min-w-[400px] max-w-[90vw]">
                <h2 className="font-mono text-lg mb-4">Edit Timers for Step {stepIndex + 1}</h2>
                
                {/* Active Timer (Red) */}
                <div className="mb-4 p-3 rounded" style={{ backgroundColor: '#fee2e2', border: '1px solid #fca5a5' }}>
                    <label className="block font-mono text-xs mb-2 font-bold" style={{ color: '#991b1b' }}>ACTIVE TIME</label>
                    <input
                        type="text"
                        value={activeLabel}
                        onChange={e => setActiveLabel(e.target.value)}
                        placeholder="e.g., Whisking to stiff peaks"
                        className="w-full px-2 py-1 border border-neutral-300 rounded font-mono text-sm mb-2"
                    />
                    <div className="flex gap-2 items-center">
                        <div className="flex-1">
                            <label className="block font-mono text-xs mb-1">Minutes</label>
                            <input
                                type="number"
                                min="0"
                                value={activeMinutes}
                                onChange={e => setActiveMinutes(Math.max(0, parseInt(e.target.value) || 0))}
                                className="w-full px-2 py-1 border border-neutral-300 rounded font-mono text-sm"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block font-mono text-xs mb-1">Seconds</label>
                            <input
                                type="number"
                                min="0"
                                max="59"
                                value={activeSeconds}
                                onChange={e => setActiveSeconds(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                                className="w-full px-2 py-1 border border-neutral-300 rounded font-mono text-sm"
                            />
                        </div>
                    </div>
                </div>

                {/* Inactive Timer (Blue) */}
                <div className="mb-4 p-3 rounded" style={{ backgroundColor: '#dbeafe', border: '1px solid #93c5fd' }}>
                    <label className="block font-mono text-xs mb-2 font-bold" style={{ color: '#1e40af' }}>INACTIVE TIME</label>
                    <input
                        type="text"
                        value={inactiveLabel}
                        onChange={e => setInactiveLabel(e.target.value)}
                        placeholder="e.g., Baking until golden brown"
                        className="w-full px-2 py-1 border border-neutral-300 rounded font-mono text-sm mb-2"
                    />
                    <div className="flex gap-2 items-center">
                        <div className="flex-1">
                            <label className="block font-mono text-xs mb-1">Minutes</label>
                            <input
                                type="number"
                                min="0"
                                value={inactiveMinutes}
                                onChange={e => setInactiveMinutes(Math.max(0, parseInt(e.target.value) || 0))}
                                className="w-full px-2 py-1 border border-neutral-300 rounded font-mono text-sm"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block font-mono text-xs mb-1">Seconds</label>
                            <input
                                type="number"
                                min="0"
                                max="59"
                                value={inactiveSeconds}
                                onChange={e => setInactiveSeconds(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                                className="w-full px-2 py-1 border border-neutral-300 rounded font-mono text-sm"
                            />
                        </div>
                    </div>
                </div>

                {/* Total Duration Display */}
                <div className="mb-4 p-2 bg-neutral-50 rounded text-center">
                    <span className="font-mono text-xs text-neutral-600">Total: {totalMinutes}m {totalSeconds}s</span>
                </div>

                <div className="flex gap-2 justify-end">
                    <button
                        className="px-4 py-2 bg-neutral-200 text-neutral-900 rounded font-mono text-xs hover:bg-neutral-300"
                        onClick={onClose}
                    >Cancel</button>
                    {onRemove && (
                        <button
                            className="px-4 py-2 bg-red-600 text-white rounded font-mono text-xs hover:bg-red-700"
                            onClick={onRemove}
                        >Remove</button>
                    )}
                    <button
                        className="px-4 py-2 bg-neutral-900 text-white rounded font-mono text-xs hover:bg-neutral-800"
                        onClick={handleSave}
                    >Save</button>
                </div>
            </div>
        </div>
    );
}

/**
 * RecipeComponentEditor - Reusable component for editing a single recipe component
 * 
 * @param {Object} component - The component data { id, name, ingredients, prerequisites, steps, subComponents }
 * @param {Function} onChange - Callback when component data changes
 * @param {Function} onRemove - Callback to remove this component (optional)
 * @param {boolean} isCollapsible - Whether the component can be collapsed (default: true)
 * @param {boolean} defaultExpanded - Whether the component starts expanded (default: true)
 * @param {string} accentColor - Color for the component header border (default: notecardBlue)
 */
const RecipeComponentEditor = ({
    component,
    onChange,
    onRemove,
    isCollapsible = true,
    defaultExpanded = true,
    accentColor = colorTokens.notecardBlue
}) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    const [timerModal, setTimerModal] = useState({ open: false, stepIndex: null, timingSegments: null });
    const [expandedStepIndex, setExpandedStepIndex] = useState(null);

    // Ensure component has all required fields
    const safeComponent = {
        id: component?.id || '',
        name: component?.name || '',
        ingredients: component?.ingredients || [''],
        prerequisites: component?.prerequisites || [''],
        steps: component?.steps || [''],
        subComponents: component?.subComponents || []
    };

    // Parse steps to handle both string and object formats
    const parseSteps = (steps) => {
        if (!Array.isArray(steps)) return [''];
        return steps.map(step => {
            if (typeof step === 'string') return step;
            if (step && typeof step === 'object' && step.text) return step.text;
            return '';
        });
    };

    const parsedSteps = parseSteps(safeComponent.steps);

    // Build timing segments map from steps
    const buildTimingSegmentsMap = () => {
        const map = {};
        if (Array.isArray(safeComponent.steps)) {
            safeComponent.steps.forEach((step, index) => {
                if (step && typeof step === 'object' && step.timing_segments) {
                    map[index] = step.timing_segments;
                }
            });
        }
        return map;
    };

    const [stepTimingSegments, setStepTimingSegments] = useState(buildTimingSegmentsMap);

    // Update timing segments when component changes
    useEffect(() => {
        setStepTimingSegments(buildTimingSegmentsMap());
    }, [component?.steps]);

    const handleNameChange = (value) => {
        onChange({ ...safeComponent, name: value });
    };

    const handleListChange = (listName, index, value) => {
        const newList = [...safeComponent[listName]];
        if (listName === 'steps') {
            // Preserve timing_segments when editing step text
            const existingStep = safeComponent.steps[index];
            if (existingStep && typeof existingStep === 'object') {
                newList[index] = { ...existingStep, text: value };
            } else {
                newList[index] = value;
            }
        } else {
            newList[index] = value;
        }
        onChange({ ...safeComponent, [listName]: newList });
    };

    const addListItem = (listName) => {
        const newList = [...safeComponent[listName], listName === 'steps' ? '' : ''];
        onChange({ ...safeComponent, [listName]: newList });
    };

    const removeListItem = (listName, index) => {
        if (safeComponent[listName].length > 1 || listName === 'prerequisites') {
            const newList = safeComponent[listName].filter((_, i) => i !== index);
            onChange({ ...safeComponent, [listName]: newList.length > 0 ? newList : [''] });
            
            // Also remove timing segments for this step
            if (listName === 'steps') {
                const newTimingSegments = { ...stepTimingSegments };
                delete newTimingSegments[index];
                // Re-index remaining segments
                const reindexed = {};
                Object.keys(newTimingSegments).forEach(key => {
                    const keyNum = parseInt(key);
                    if (keyNum > index) {
                        reindexed[keyNum - 1] = newTimingSegments[key];
                    } else {
                        reindexed[keyNum] = newTimingSegments[key];
                    }
                });
                setStepTimingSegments(reindexed);
            }
        }
    };

    const handleTimerSave = (newSegments) => {
        const stepIndex = timerModal.stepIndex;
        const newTimingSegments = { ...stepTimingSegments, [stepIndex]: newSegments };
        setStepTimingSegments(newTimingSegments);
        
        // Update the step with timing_segments
        const newSteps = [...safeComponent.steps];
        const existingStep = newSteps[stepIndex];
        if (typeof existingStep === 'string') {
            newSteps[stepIndex] = { text: existingStep, timing_segments: newSegments };
        } else if (existingStep && typeof existingStep === 'object') {
            newSteps[stepIndex] = { ...existingStep, timing_segments: newSegments };
        }
        onChange({ ...safeComponent, steps: newSteps });
        setTimerModal({ open: false, stepIndex: null, timingSegments: null });
    };

    const handleTimerRemove = () => {
        const stepIndex = timerModal.stepIndex;
        const newTimingSegments = { ...stepTimingSegments };
        delete newTimingSegments[stepIndex];
        setStepTimingSegments(newTimingSegments);
        
        // Remove timing_segments from step
        const newSteps = [...safeComponent.steps];
        const existingStep = newSteps[stepIndex];
        if (existingStep && typeof existingStep === 'object') {
            const { timing_segments, ...rest } = existingStep;
            newSteps[stepIndex] = rest.text || rest;
        }
        onChange({ ...safeComponent, steps: newSteps });
        setTimerModal({ open: false, stepIndex: null, timingSegments: null });
    };

    return (
        <div className="bg-neutral-0 hairline mb-4">
            {/* Component Header */}
            <div 
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-neutral-50 transition-colors"
                style={{ borderLeft: `4px solid ${accentColor}` }}
                onClick={() => isCollapsible && setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center gap-3 flex-1">
                    {isCollapsible && (
                        <span className="font-mono text-sm text-neutral-600">
                            {isExpanded ? '▼' : '▶'}
                        </span>
                    )}
                    <input
                        type="text"
                        value={safeComponent.name}
                        onChange={(e) => handleNameChange(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="flex-1 text-lg font-mono tracking-tight bg-transparent border-none outline-none focus:bg-neutral-50 px-2 py-1"
                        style={{ color: colorTokens.oxfordBlue }}
                        placeholder="Component Name (e.g., Pie Crust)"
                    />
                </div>
                {onRemove && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemove();
                        }}
                        className="px-3 py-1 bg-red-600 text-white text-xs font-mono uppercase tracking-wider hover:bg-red-700 transition-colors"
                    >
                        Remove
                    </button>
                )}
            </div>

            {/* Component Content */}
            {isExpanded && (
                <div className="p-4 pt-0 space-y-6">
                    {/* Ingredients */}
                    <div>
                        <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-3">INGREDIENTS</div>
                        <div className="space-y-2">
                            {safeComponent.ingredients.map((ingredient, index) => (
                                <div key={index} className="flex items-start">
                                    <span className="font-mono text-sm text-neutral-900 opacity-60 mr-3 flex-shrink-0 mt-1">
                                        {index + 1}.
                                    </span>
                                    <textarea
                                        value={ingredient}
                                        onChange={(e) => handleListChange('ingredients', index, e.target.value)}
                                        placeholder="Enter ingredient with quantity (e.g., 2 cups flour)"
                                        rows={1}
                                        className="flex-1 px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:bg-neutral-50 resize-none text-sm"
                                        style={{ height: 'auto', minHeight: '20px' }}
                                        onInput={(e) => {
                                            e.target.style.height = 'auto';
                                            e.target.style.height = e.target.scrollHeight + 'px';
                                        }}
                                    />
                                    {safeComponent.ingredients.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeListItem('ingredients', index)}
                                            className="w-6 h-6 bg-red-600 text-white text-xs hover:bg-red-700 transition-colors ml-2"
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={() => addListItem('ingredients')}
                            className="mt-2 px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                        >
                            + ADD INGREDIENT
                        </button>
                    </div>

                    {/* Section Separator */}
                    <div className="border-t" style={{ borderColor: colorTokens.notecardBlue }}></div>

                    {/* Prerequisites */}
                    <div>
                        <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-3">PREREQUISITES</div>
                        <Text className="mb-3 opacity-60 text-sm">
                            Preparation steps for this component before cooking begins.
                        </Text>
                        <div className="space-y-2">
                            {safeComponent.prerequisites.map((prerequisite, index) => (
                                <div key={index} className="flex items-start">
                                    <span className="font-mono text-xs text-neutral-900 opacity-60 mr-2 flex-shrink-0 mt-1">
                                        {index + 1}.
                                    </span>
                                    <textarea
                                        value={prerequisite}
                                        onChange={(e) => handleListChange('prerequisites', index, e.target.value)}
                                        placeholder="e.g., Bring butter to room temperature"
                                        rows={1}
                                        className="flex-1 px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:bg-neutral-50 resize-none text-sm"
                                        style={{ height: 'auto', minHeight: '20px' }}
                                        onInput={(e) => {
                                            e.target.style.height = 'auto';
                                            e.target.style.height = e.target.scrollHeight + 'px';
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeListItem('prerequisites', index)}
                                        className="w-6 h-6 bg-red-600 text-white text-xs hover:bg-red-700 transition-colors ml-2"
                                    >
                                        ✕
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={() => addListItem('prerequisites')}
                            className="mt-2 px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                        >
                            + ADD PREREQUISITE
                        </button>
                    </div>

                    {/* Section Separator */}
                    <div className="border-t" style={{ borderColor: colorTokens.notecardBlue }}></div>

                    {/* Steps */}
                    <div>
                        <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-3">STEPS</div>
                        <Text className="mb-3 opacity-60 text-sm">
                            Step-by-step instructions for this component.
                        </Text>
                        <div className="space-y-3">
                            {parsedSteps.map((step, index) => (
                                <div key={index} className="flex items-start">
                                    <span className="font-mono text-sm font-bold text-neutral-900 mr-3 flex-shrink-0 mt-1">
                                        {index + 1}
                                    </span>
                                    <textarea
                                        value={step}
                                        onChange={(e) => handleListChange('steps', index, e.target.value)}
                                        placeholder="Describe this cooking step in detail"
                                        rows={expandedStepIndex === index ? 5 : 1}
                                        className="flex-1 px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:bg-neutral-50 resize-none text-sm"
                                        style={{ height: 'auto', minHeight: '20px' }}
                                        onInput={(e) => {
                                            if (expandedStepIndex !== index) {
                                                e.target.style.height = 'auto';
                                                e.target.style.height = e.target.scrollHeight + 'px';
                                            }
                                        }}
                                        onDoubleClick={() => {
                                            setExpandedStepIndex(expandedStepIndex === index ? null : index);
                                        }}
                                    />
                                    {parsedSteps.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeListItem('steps', index)}
                                            className="w-6 h-6 bg-red-600 text-white text-xs hover:bg-red-700 transition-colors ml-2"
                                        >
                                            ✕
                                        </button>
                                    )}
                                    {/* Timer Display */}
                                    <div className="flex items-center ml-2 h-8">
                                        {(() => {
                                            const segments = stepTimingSegments[index] || [];
                                            const activeSegment = segments.find(s => s.kind === 'active');
                                            const inactiveSegment = segments.find(s => s.kind === 'inactive');
                                            const hasTimers = (activeSegment && inactiveSegment);
                                            
                                            if (hasTimers) {
                                                return (
                                                    <div className="flex items-center gap-1">
                                                        <button
                                                            type="button"
                                                            onClick={() => setTimerModal({ open: true, stepIndex: index, timingSegments: segments })}
                                                            className="flex items-center px-2 py-1 text-xs font-mono transition-colors h-8"
                                                            style={{
                                                                backgroundColor: '#fee2e2',
                                                                border: '1px solid #fca5a5',
                                                                color: '#991b1b'
                                                            }}
                                                            title={activeSegment.label || 'Active time'}
                                                        >
                                                            <TimerIcon size={14} className="mr-1" />
                                                            {activeSegment.expectedSeconds > 0 ? `${Math.floor(activeSegment.expectedSeconds / 60)}m` : '0m'}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setTimerModal({ open: true, stepIndex: index, timingSegments: segments })}
                                                            className="flex items-center px-2 py-1 text-xs font-mono transition-colors h-8"
                                                            style={{
                                                                backgroundColor: '#dbeafe',
                                                                border: '1px solid #93c5fd',
                                                                color: '#1e40af'
                                                            }}
                                                            title={inactiveSegment.label || 'Inactive time'}
                                                        >
                                                            <TimerIcon size={14} className="mr-1" />
                                                            {inactiveSegment.expectedSeconds > 0 ? `${Math.floor(inactiveSegment.expectedSeconds / 60)}m` : '0m'}
                                                        </button>
                                                    </div>
                                                );
                                            }
                                            return (
                                                <button
                                                    type="button"
                                                    className="flex items-center px-2 py-1 bg-neutral-50 text-neutral-600 hairline hover:bg-neutral-100 transition-colors h-8 text-xs font-mono"
                                                    onClick={() => setTimerModal({ open: true, stepIndex: index, timingSegments: [] })}
                                                    title="Add timers"
                                                >
                                                    <TimerIcon size={16} />
                                                </button>
                                            );
                                        })()}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={() => addListItem('steps')}
                            className="mt-2 px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                        >
                            + ADD STEP
                        </button>
                    </div>
                </div>
            )}

            {/* Timer Edit Modal */}
            <TimerEditModal
                open={timerModal.open}
                onClose={() => setTimerModal({ open: false, stepIndex: null, timingSegments: null })}
                onSave={handleTimerSave}
                onRemove={handleTimerRemove}
                stepIndex={timerModal.stepIndex}
                timingSegments={timerModal.timingSegments}
            />
        </div>
    );
};

/**
 * AssemblyEditor - Editor for the assembly section (combining components)
 * Only has prerequisites (optional) and steps - NO ingredients
 */
export const AssemblyEditor = ({
    assembly,
    onChange,
    componentNames = []
}) => {
    const [timerModal, setTimerModal] = useState({ open: false, stepIndex: null, timingSegments: null });
    const [expandedStepIndex, setExpandedStepIndex] = useState(null);

    const safeAssembly = {
        prerequisites: assembly?.prerequisites || [''],
        steps: assembly?.steps || ['']
    };

    // Parse steps to handle both string and object formats
    const parseSteps = (steps) => {
        if (!Array.isArray(steps)) return [''];
        return steps.map(step => {
            if (typeof step === 'string') return step;
            if (step && typeof step === 'object' && step.text) return step.text;
            return '';
        });
    };

    const parsedSteps = parseSteps(safeAssembly.steps);

    // Build timing segments map from steps
    const buildTimingSegmentsMap = () => {
        const map = {};
        if (Array.isArray(safeAssembly.steps)) {
            safeAssembly.steps.forEach((step, index) => {
                if (step && typeof step === 'object' && step.timing_segments) {
                    map[index] = step.timing_segments;
                }
            });
        }
        return map;
    };

    const [stepTimingSegments, setStepTimingSegments] = useState(buildTimingSegmentsMap);

    useEffect(() => {
        setStepTimingSegments(buildTimingSegmentsMap());
    }, [assembly?.steps]);

    const handleListChange = (listName, index, value) => {
        const newList = [...safeAssembly[listName]];
        if (listName === 'steps') {
            const existingStep = safeAssembly.steps[index];
            if (existingStep && typeof existingStep === 'object') {
                newList[index] = { ...existingStep, text: value };
            } else {
                newList[index] = value;
            }
        } else {
            newList[index] = value;
        }
        onChange({ ...safeAssembly, [listName]: newList });
    };

    const addListItem = (listName) => {
        const newList = [...safeAssembly[listName], ''];
        onChange({ ...safeAssembly, [listName]: newList });
    };

    const removeListItem = (listName, index) => {
        const newList = safeAssembly[listName].filter((_, i) => i !== index);
        onChange({ ...safeAssembly, [listName]: newList.length > 0 ? newList : [''] });
    };

    const handleTimerSave = (newSegments) => {
        const stepIndex = timerModal.stepIndex;
        const newTimingSegments = { ...stepTimingSegments, [stepIndex]: newSegments };
        setStepTimingSegments(newTimingSegments);
        
        const newSteps = [...safeAssembly.steps];
        const existingStep = newSteps[stepIndex];
        if (typeof existingStep === 'string') {
            newSteps[stepIndex] = { text: existingStep, timing_segments: newSegments };
        } else if (existingStep && typeof existingStep === 'object') {
            newSteps[stepIndex] = { ...existingStep, timing_segments: newSegments };
        }
        onChange({ ...safeAssembly, steps: newSteps });
        setTimerModal({ open: false, stepIndex: null, timingSegments: null });
    };

    const handleTimerRemove = () => {
        const stepIndex = timerModal.stepIndex;
        const newTimingSegments = { ...stepTimingSegments };
        delete newTimingSegments[stepIndex];
        setStepTimingSegments(newTimingSegments);
        
        const newSteps = [...safeAssembly.steps];
        const existingStep = newSteps[stepIndex];
        if (existingStep && typeof existingStep === 'object') {
            const { timing_segments, ...rest } = existingStep;
            newSteps[stepIndex] = rest.text || rest;
        }
        onChange({ ...safeAssembly, steps: newSteps });
        setTimerModal({ open: false, stepIndex: null, timingSegments: null });
    };

    return (
        <div className="bg-neutral-0 hairline p-4">
            <div className="mb-4" style={{ borderLeft: `4px solid ${colorTokens.oxfordBlue}`, paddingLeft: '12px' }}>
                <div className="font-mono text-lg uppercase tracking-wider" style={{ color: colorTokens.oxfordBlue }}>
                    ASSEMBLY
                </div>
                <Text className="text-sm opacity-60 mt-1">
                    Steps to combine the components above into the final dish. No ingredients here - all ingredients are in the components.
                </Text>
            </div>

            {/* Prerequisites (optional) */}
            <div className="mb-6">
                <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-3">PREREQUISITES (OPTIONAL)</div>
                <div className="space-y-2">
                    {safeAssembly.prerequisites.map((prerequisite, index) => (
                        <div key={index} className="flex items-start">
                            <span className="font-mono text-xs text-neutral-900 opacity-60 mr-2 flex-shrink-0 mt-1">
                                {index + 1}.
                            </span>
                            <textarea
                                value={prerequisite}
                                onChange={(e) => handleListChange('prerequisites', index, e.target.value)}
                                placeholder="e.g., Have pie dish ready"
                                rows={1}
                                className="flex-1 px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:bg-neutral-50 resize-none text-sm"
                                style={{ height: 'auto', minHeight: '20px' }}
                                onInput={(e) => {
                                    e.target.style.height = 'auto';
                                    e.target.style.height = e.target.scrollHeight + 'px';
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => removeListItem('prerequisites', index)}
                                className="w-6 h-6 bg-red-600 text-white text-xs hover:bg-red-700 transition-colors ml-2"
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                </div>
                <button
                    type="button"
                    onClick={() => addListItem('prerequisites')}
                    className="mt-2 px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                >
                    + ADD PREREQUISITE
                </button>
            </div>

            {/* Section Separator */}
            <div className="border-t mb-6" style={{ borderColor: colorTokens.oxfordBlue }}></div>

            {/* Assembly Steps */}
            <div>
                <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-3">ASSEMBLY STEPS</div>
                <Text className="mb-3 opacity-60 text-sm">
                    Describe how to combine the components together.
                    {componentNames.length > 0 && ` Available components: ${componentNames.join(', ')}.`}
                </Text>
                <div className="space-y-3">
                    {parsedSteps.map((step, index) => (
                        <div key={index} className="flex items-start">
                            <span className="font-mono text-sm font-bold text-neutral-900 mr-3 flex-shrink-0 mt-1">
                                {index + 1}
                            </span>
                            <textarea
                                value={step}
                                onChange={(e) => handleListChange('steps', index, e.target.value)}
                                placeholder="e.g., Place crust in dish, add filling, bake at 375°F for 45 minutes"
                                rows={expandedStepIndex === index ? 5 : 1}
                                className="flex-1 px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:bg-neutral-50 resize-none text-sm"
                                style={{ height: 'auto', minHeight: '20px' }}
                                onInput={(e) => {
                                    if (expandedStepIndex !== index) {
                                        e.target.style.height = 'auto';
                                        e.target.style.height = e.target.scrollHeight + 'px';
                                    }
                                }}
                                onDoubleClick={() => {
                                    setExpandedStepIndex(expandedStepIndex === index ? null : index);
                                }}
                            />
                            {parsedSteps.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => removeListItem('steps', index)}
                                    className="w-6 h-6 bg-red-600 text-white text-xs hover:bg-red-700 transition-colors ml-2"
                                >
                                    ✕
                                </button>
                            )}
                            {/* Timer Display */}
                            <div className="flex items-center ml-2 h-8">
                                {(() => {
                                    const segments = stepTimingSegments[index] || [];
                                    const activeSegment = segments.find(s => s.kind === 'active');
                                    const inactiveSegment = segments.find(s => s.kind === 'inactive');
                                    const hasTimers = (activeSegment && inactiveSegment);
                                    
                                    if (hasTimers) {
                                        return (
                                            <div className="flex items-center gap-1">
                                                <button
                                                    type="button"
                                                    onClick={() => setTimerModal({ open: true, stepIndex: index, timingSegments: segments })}
                                                    className="flex items-center px-2 py-1 text-xs font-mono transition-colors h-8"
                                                    style={{
                                                        backgroundColor: '#fee2e2',
                                                        border: '1px solid #fca5a5',
                                                        color: '#991b1b'
                                                    }}
                                                    title={activeSegment.label || 'Active time'}
                                                >
                                                    <TimerIcon size={14} className="mr-1" />
                                                    {activeSegment.expectedSeconds > 0 ? `${Math.floor(activeSegment.expectedSeconds / 60)}m` : '0m'}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setTimerModal({ open: true, stepIndex: index, timingSegments: segments })}
                                                    className="flex items-center px-2 py-1 text-xs font-mono transition-colors h-8"
                                                    style={{
                                                        backgroundColor: '#dbeafe',
                                                        border: '1px solid #93c5fd',
                                                        color: '#1e40af'
                                                    }}
                                                    title={inactiveSegment.label || 'Inactive time'}
                                                >
                                                    <TimerIcon size={14} className="mr-1" />
                                                    {inactiveSegment.expectedSeconds > 0 ? `${Math.floor(inactiveSegment.expectedSeconds / 60)}m` : '0m'}
                                                </button>
                                            </div>
                                        );
                                    }
                                    return (
                                        <button
                                            type="button"
                                            className="flex items-center px-2 py-1 bg-neutral-50 text-neutral-600 hairline hover:bg-neutral-100 transition-colors h-8 text-xs font-mono"
                                            onClick={() => setTimerModal({ open: true, stepIndex: index, timingSegments: [] })}
                                            title="Add timers"
                                        >
                                            <TimerIcon size={16} />
                                        </button>
                                    );
                                })()}
                            </div>
                        </div>
                    ))}
                </div>
                <button
                    type="button"
                    onClick={() => addListItem('steps')}
                    className="mt-2 px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                >
                    + ADD ASSEMBLY STEP
                </button>
            </div>

            {/* Timer Edit Modal */}
            <TimerEditModal
                open={timerModal.open}
                onClose={() => setTimerModal({ open: false, stepIndex: null, timingSegments: null })}
                onSave={handleTimerSave}
                onRemove={handleTimerRemove}
                stepIndex={timerModal.stepIndex}
                timingSegments={timerModal.timingSegments}
            />
        </div>
    );
};

export default RecipeComponentEditor;

